import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/utils/format';
import type { Wallet } from '@/types/portal';

export type WalletAction = 'edit' | 'activate' | 'deactivate' | 'delete';

interface WalletManageModalProps {
  wallet: Wallet | null;
  action: WalletAction;
  busy: boolean;
  onClose: () => void;
  onSave: (details: { name: string; description: string }) => void;
  onConfirm: () => void;
}

const COPY: Record<Exclude<WalletAction, 'edit'>, { title: string; icon: string; button: string; body: string }> = {
  activate: {
    title: 'Activate wallet',
    icon: 'ri-play-circle-line',
    button: 'Activate',
    body: 'Payments and bulk transfers can be sent from this wallet again.',
  },
  deactivate: {
    title: 'Deactivate wallet',
    icon: 'ri-pause-circle-line',
    button: 'Deactivate',
    body: "No payments can be sent from this wallet until it is activated again. Its balance stays where it is.",
  },
  delete: {
    title: 'Delete wallet',
    icon: 'ri-delete-bin-line',
    button: 'Delete wallet',
    body: 'The wallet is removed from the agency. This cannot be undone.',
  },
};

export default function WalletManageModal({ wallet, action, busy, onClose, onSave, onConfirm }: WalletManageModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (wallet) {
      setName(wallet.ownerName);
      setDescription(wallet.description ?? '');
    }
  }, [wallet]);

  if (!wallet) return null;

  if (action === 'edit') {
    const valid = name.trim().length > 0;
    return (
      <Modal
        open
        onClose={onClose}
        icon="ri-edit-line"
        title="Edit wallet"
        subtitle={`${wallet.currency} funding wallet`}
        widthClassName="max-w-md"
        footer={
          <>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              icon="ri-check-line"
              disabled={!valid || busy}
              onClick={() => onSave({ name: name.trim(), description: description.trim() })}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="wallet-name" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
              Wallet name
            </label>
            <input
              id="wallet-name"
              type="text"
              value={name}
              maxLength={255}
              onChange={(event) => setName(event.target.value)}
              className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="wallet-desc" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
              Description
            </label>
            <textarea
              id="wallet-desc"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              maxLength={500}
              placeholder="What this wallet is used for"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
        </div>
      </Modal>
    );
  }

  const copy = COPY[action];
  const blocked = action === 'delete' && wallet.balance > 0;

  return (
    <Modal
      open
      onClose={onClose}
      icon={copy.icon}
      title={copy.title}
      subtitle={wallet.ownerName}
      widthClassName="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button icon={copy.icon} disabled={blocked || busy} onClick={onConfirm}>
            {busy ? 'Working…' : copy.button}
          </Button>
        </>
      }
    >
      {blocked ? (
        <p className="flex items-start gap-2 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          This wallet still holds {formatMoney(wallet.balance, wallet.currency)}. Move the balance out before deleting it.
        </p>
      ) : (
        <p className="text-sm text-foreground-700">{copy.body}</p>
      )}
    </Modal>
  );
}
